const SCRIPT_URL_ALLOWLIST = [
  'https://www.googletagmanager.com',
  'https://maps.googleapis.com',
  'https://maps.gstatic.com',
];
const REACT_INERT_HTML = new Set(['<script></script>']);

export function isAllowedDefaultHtml(value: string): boolean {
  // JSON-LD uses serializeJsonLd(), which escapes '<'; values without it cannot create markup.
  return REACT_INERT_HTML.has(value) || !value.includes('<');
}

type TrustedPolicyLike = {
  createHTML(value: string): unknown;
  createScriptURL(value: string): unknown;
};

declare global {
  interface Window {
    trustedTypes?: {
      createPolicy(
        name: string,
        rules: {
          createHTML?: (value: string) => string | null;
          createScriptURL?: (value: string) => string | null;
          createScript?: (value: string) => string | null;
        },
      ): TrustedPolicyLike;
    };
  }
}

let appPolicy: TrustedPolicyLike | null = null;

function isAllowedScriptUrl(value: string): boolean {
  try {
    const origin = new URL(value, window.location.href).origin;
    return origin === window.location.origin || SCRIPT_URL_ALLOWLIST.includes(origin);
  } catch {
    return false;
  }
}

export function installTrustedTypesPolicies(): void {
  if (typeof window === 'undefined' || !window.trustedTypes || appPolicy) return;

  appPolicy = window.trustedTypes.createPolicy('tea-web', {
    createHTML: (value: string) => value,
    createScriptURL: (value: string) => {
      if (!isAllowedScriptUrl(value)) throw new TypeError('Blocked script URL by tea-web policy.');
      return value;
    },
  });

  try {
    window.trustedTypes.createPolicy('default', {
      createHTML: (value: string) => (isAllowedDefaultHtml(value) ? value : null),
      createScriptURL: (value: string) => (isAllowedScriptUrl(value) ? value : null),
      // React's development Flight client evals strings to rebuild Server Component stacks.
      // Production builds never eval, so they keep rejecting every script string.
      createScript: (value: string) =>
        process.env.NODE_ENV === 'development' ? value : null,
    });
  } catch (error) {
    // Another entry may have installed the shared default policy already.
    if (!(error instanceof TypeError)) throw error;
  }
}

export function toTrustedHTML(value: string): unknown {
  return appPolicy ? appPolicy.createHTML(value) : value;
}

export function toTrustedScriptURL(value: string): unknown {
  return appPolicy ? appPolicy.createScriptURL(value) : value;
}

if (typeof window !== 'undefined') installTrustedTypesPolicies();
